/**
 * Whether a bead-order block sits fully inside a page-sized column box.
 * Items are checked in source order so packing stays top-to-bottom, left-to-right.
 */
export function itemFitsPdfPage(pageBox, itemBox) {
  if (!pageBox || !itemBox) return false;
  return (
    itemBox.bottom <= pageBox.bottom + 1 &&
    itemBox.right <= pageBox.right + 1 &&
    itemBox.left >= pageBox.left - 1 &&
    itemBox.top >= pageBox.top - 1
  );
}

/**
 * How many leading items fit on one page of columns. Stops at the first that
 * does not fit so later runs are not reordered ahead of earlier ones.
 */
export function countFittingOrderItems(pageBox, itemBoxes) {
  const boxes = Array.isArray(itemBoxes) ? itemBoxes : [];
  let count = 0;
  for (const box of boxes) {
    if (!itemFitsPdfPage(pageBox, box)) break;
    count += 1;
  }
  return count;
}

function measureBox(el) {
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
  };
}

/**
 * Capture the stringing-order list as one canvas per PDF page. Each page uses
 * its own column layout (fill down, then right) instead of slicing one tall
 * balanced column block across pages.
 */
export async function captureBeadOrderByPage(
  orderEl,
  { pageHeightPx, html2canvas, captureOptions = {} }
) {
  if (!orderEl || typeof html2canvas !== "function") return [];

  const children = Array.from(orderEl.children);
  if (children.length === 0) return [];

  const widthPx = Math.max(
    1,
    Math.round(orderEl.getBoundingClientRect().width || orderEl.offsetWidth || 1)
  );
  const heightPx = Math.max(40, Math.round(Number(pageHeightPx) || 0));

  const host = document.createElement("div");
  host.setAttribute("data-bead-order-pdf-host", "true");
  host.style.cssText = [
    "position:fixed",
    "left:-10000px",
    "top:0",
    `width:${widthPx}px`,
    "pointer-events:none",
    "visibility:hidden",
  ].join(";");
  document.body.appendChild(host);

  const canvases = [];
  let index = 0;

  try {
    while (index < children.length) {
      const page = document.createElement("ol");
      page.className = `${orderEl.className} bead-order--pdf-page`.trim();
      page.style.width = `${widthPx}px`;
      page.style.height = `${heightPx}px`;
      host.style.visibility = "hidden";
      host.appendChild(page);

      const start = index;
      let fitted = 0;

      while (index < children.length) {
        const clone = children[index].cloneNode(true);
        page.appendChild(clone);
        // Force layout before measuring.
        void page.offsetHeight;
        const fits = itemFitsPdfPage(measureBox(page), measureBox(clone));
        if (!fits) {
          page.removeChild(clone);
          if (fitted === 0) {
            // One block taller than the page: still emit it alone.
            page.appendChild(clone);
            index += 1;
            fitted = 1;
          }
          break;
        }
        fitted += 1;
        index += 1;
      }

      if (fitted === 0) break;

      host.style.visibility = "visible";
      const canvas = await html2canvas(page, captureOptions);
      if (canvas && canvas.width > 0 && canvas.height > 0) {
        canvases.push(canvas);
      }
      host.removeChild(page);

      if (index <= start) break;
    }
  } finally {
    host.remove();
  }

  return canvases;
}
